import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createDraft } from '../repository/draftsRepository';

interface DrafterEntry {
    username: string;
    error?: string;
}

const CreateDraftForm: React.FC = () => {
    const [name, setName] = useState('');
    const [drafters, setDrafters] = useState<DrafterEntry[]>([]);
    const [newDrafter, setNewDrafter] = useState('');
    const [numRounds, setNumRounds] = useState(5);
    const [notifyOnTurn, setNotifyOnTurn] = useState(true);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const addDrafter = () => {
        const username = newDrafter.trim();
        if (!username) return;

        if (drafters.some(d => d.username.toLowerCase() === username.toLowerCase())) {
            return;
        }

        setDrafters([...drafters, { username }]);
        setNewDrafter('');
    };

    const removeDrafter = (index: number) => {
        setDrafters(drafters.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addDrafter();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Clear previous per-drafter errors
        setDrafters(drafters.map(d => ({ ...d, error: undefined })));

        if (!name.trim()) {
            setError('Draft name is required');
            return;
        }

        if (drafters.length < 1) {
            setError('Add at least one other drafter');
            return;
        }

        if (numRounds < 1) {
            setError('At least 1 round is required');
            return;
        }

        setLoading(true);

        const result = await createDraft(
            name.trim(),
            drafters.map(d => d.username),
            numRounds,
            notifyOnTurn,
            description.trim()
        );

        if ('error' in result) {
            // Check if it's an "Unknown usernames" error and parse it
            const unknownMatch = result.error.match(/Unknown usernames?: (.+)/);
            if (unknownMatch) {
                const unknownUsernames = unknownMatch[1].split(',').map(u => u.trim().toLowerCase());
                setDrafters(drafters.map(d => ({
                    ...d,
                    error: unknownUsernames.includes(d.username.toLowerCase())
                        ? 'User not found'
                        : undefined
                })));
                setError('Some usernames were not found');
            } else {
                setError(result.error);
            }
            setLoading(false);
            return;
        }

        navigate(`/drafts/${result.draft.uuid}`);
    };

    return (
        <div className="create-draft-container">
            <div className="form-header">
                <Link to="/" className="back-link">Back to Drafts</Link>
                <h2>Create New Draft</h2>
            </div>

            <form onSubmit={handleSubmit} className="create-draft-form">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label htmlFor="name">Draft Name</label>
                    <input
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., 2024 Movie Draft"
                        disabled={loading}
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Other Drafters</label>
                    <div className="drafter-input-group">
                        <input
                            type="text"
                            value={newDrafter}
                            onChange={(e) => setNewDrafter(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter username"
                            disabled={loading}
                        />
                        <button
                            type="button"
                            className="btn-secondary btn-small"
                            onClick={addDrafter}
                            disabled={loading || !newDrafter.trim()}
                        >
                            Add
                        </button>
                    </div>
                    {drafters.length > 0 && (
                        <div className="drafter-chips">
                            {drafters.map((drafter, index) => (
                                <div
                                    key={index}
                                    className={`drafter-chip ${drafter.error ? 'error' : ''}`}
                                >
                                    <span className="drafter-chip-name">{drafter.username}</span>
                                    {drafter.error && (
                                        <span className="drafter-chip-error">{drafter.error}</span>
                                    )}
                                    <button
                                        type="button"
                                        className="drafter-chip-remove"
                                        onClick={() => removeDrafter(index)}
                                        disabled={loading}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="form-help">
                        You'll be added automatically as the draft owner.
                    </p>
                </div>

                <div className="form-group">
                    <label htmlFor="numRounds">Number of Rounds</label>
                    <input
                        type="number"
                        id="numRounds"
                        value={numRounds}
                        onChange={(e) => setNumRounds(parseInt(e.target.value) || 1)}
                        min={1}
                        max={50}
                        disabled={loading}
                    />
                </div>

                <div className="form-group checkbox-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={notifyOnTurn}
                            onChange={(e) => setNotifyOnTurn(e.target.checked)}
                            disabled={loading}
                        />
                        Email drafters when it's their turn
                    </label>
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description (optional)</label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe the draft rules, theme, or any notes for participants"
                        disabled={loading}
                        rows={3}
                    />
                </div>

                <div className="form-actions">
                    <Link to="/" className="btn-secondary">Cancel</Link>
                    <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Draft'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateDraftForm;
